import { hasSubanalysisInvocation } from "@/domains/templates/subanalysis-capability";
import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { queryOptions } from "@tanstack/react-query";
import { fetchTemplates } from "@/domains/templates/api";
import { toUiItem as toTemplateUiItem } from "@/domains/templates/mappers";
import {
  type AnalysisRow,
  type AnalysisStepRow,
  type Json,
} from "@/types/supabase.types";

export type ReportStatus = AnalysisRow["analysis_status"];

export type UiReportItem = {
  id: string;
  name: string;
  status: ReportStatus;
  createdAt: string;
  type: string;
  context?: string;
  isExample: boolean;
};

export type UiReportStepRow = AnalysisStepRow & {
  display_name?: string | null;
  displayInputResults: InputResultLabel[];
  templateOrigin?: TemplateOrigin;
};

export type TemplateOrigin = {
  id: string;
  name: string;
  href: string;
};

export function toTemplateOrigin({
  id,
  parentId,
  displayName,
  technicalName,
}: {
  id: string;
  parentId?: string;
  displayName?: string | null;
  technicalName?: string | null;
}): TemplateOrigin {
  const name = displayName?.trim() || technicalName?.trim() || id;
  return {
    id,
    name,
    href: parentId
      ? `/template-builder/${parentId}#template-step-${id}`
      : `/template-builder/${id}`,
  };
}

export type InputResultLabel = {
  key: string;
  value: string;
  references?: InputResultReference[];
  children?: InputResultLabel[];
};

export type InputResultReference = {
  id: string;
  name: string;
  href?: string;
};

export type InputResultReferenceLookups = {
  analyses: Map<string, { name: string }>;
  steps: Map<string, { name: string; analysisId: string }>;
};

type SortableInputResultLabel = InputResultLabel & {
  group: string;
  order: number;
};

type FilterOptions = {
  search?: string;
  types?: string[];
  showExamples?: boolean;
};

type ReportWithSettings = AnalysisRow & {
  analysis_setting: {
    display_name: string;
    display_description: string | null;
  };
};

export async function fetchReports(
  options: FilterOptions,
  currentTenantId: string,
): Promise<{ analysis: ReportWithSettings; isExample: boolean }[]> {
  let query = analysisClient
    .from("analysis")
    .select(
      "*, analysis_setting!inner(display_name, display_description), tenant_collection!inner()",
    )
    .eq("tenant_collection.tenant_collection_name", "Standard");

  const trimmedSearch = options.search?.trim();
  if (trimmedSearch && trimmedSearch.length > 0) {
    query = query.filter(
      "analysis_setting.display_name",
      "ilike",
      `%${trimmedSearch}%`,
    );
  }
  if (options.types && options.types.length > 0) {
    query = query.in("analysis_type", options.types);
  }
  query = query.order("record_created", { ascending: false });

  const { data, error } = await query;
  if (error) throw error;

  const visibleAnalyses = options.showExamples
    ? (data ?? [])
    : (data ?? []).filter(
        (analysis) => analysis.record_tenant_id === currentTenantId,
      );
  return visibleAnalyses.map((analysis) => ({
    analysis,
    isExample: analysis.record_tenant_id !== currentTenantId,
  }));
}

/** Context is stored in display_description. */
export function extractContext(
  displayDescription: string | null | undefined,
): string | undefined {
  return typeof displayDescription === "string" && displayDescription.trim()
    ? displayDescription
    : undefined;
}

function toUiItem(item: {
  analysis: ReportWithSettings;
  isExample: boolean;
}): UiReportItem {
  const displayName = item.analysis.analysis_setting.display_name.trim();
  if (!displayName) {
    throw new Error(
      `Report ${item.analysis.analysis_id} is missing analysis_setting.display_name`,
    );
  }
  const context = extractContext(
    item.analysis.analysis_setting.display_description,
  );
  return {
    id: item.analysis.analysis_id,
    name: displayName,
    status: item.analysis.analysis_status,
    createdAt: item.analysis.record_created,
    type: item.analysis.analysis_type,
    context,
    isExample: item.isExample,
  };
}

function serializeInputResultValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const serializedItems = value
      .map((item) => serializeInputResultValue(item))
      .filter((item): item is string => Boolean(item));

    return serializedItems.length > 0 ? serializedItems.join(", ") : null;
  }

  if (value && typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }

  return null;
}

type InputVariableRow = {
  variable_id: string;
  variable_name: string;
  variable_name_frontend: string | null;
  variable_group?: string | null;
  variable_is_visible?: boolean;
  variable_order?: number;
  analysis_step_id?: string | null;
  variable_linked_table?: string | null;
  parent_variable_id?: string | null;
};

type InputVariableValueRow = {
  variable_id: string;
  variable_value: Json;
  analysis_step_id?: string | null;
};

function collectReferenceIds(
  variables: InputVariableRow[],
  values: InputVariableValueRow[],
) {
  const variablesById = new Map(variables.map((row) => [row.variable_id, row]));
  const analysisIds = new Set<string>();
  const stepIds = new Set<string>();
  for (const row of values) {
    const table = variablesById.get(row.variable_id)?.variable_linked_table;
    if (!Array.isArray(row.variable_value)) continue;
    for (const item of row.variable_value) {
      if (typeof item !== "string") continue;
      if (table === "analysis") analysisIds.add(item);
      if (table === "analysisstep") stepIds.add(item);
    }
  }
  return { analysisIds: [...analysisIds], stepIds: [...stepIds] };
}

async function fetchInputResultReferenceLookups(
  variables: InputVariableRow[],
  values: InputVariableValueRow[],
): Promise<InputResultReferenceLookups> {
  const { analysisIds, stepIds } = collectReferenceIds(variables, values);
  const [analysisResult, stepResult] = await Promise.all([
    analysisIds.length > 0
      ? analysisClient
          .from("analysis")
          .select("analysis_id, analysis_name, analysis_setting(display_name)")
          .in("analysis_id", analysisIds)
      : Promise.resolve({ data: [], error: null }),
    stepIds.length > 0
      ? analysisClient
          .from("analysisstep")
          .select(
            "analysis_step_id, analysis_id, analysis_step_name, analysisstep_setting(display_name)",
          )
          .in("analysis_step_id", stepIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (analysisResult.error) throw analysisResult.error;
  if (stepResult.error) throw stepResult.error;

  const analyses = new Map<string, { name: string }>();
  for (const row of analysisResult.data ?? []) {
    analyses.set(row.analysis_id, {
      name:
        row.analysis_setting?.display_name?.trim() ||
        row.analysis_name ||
        row.analysis_id,
    });
  }
  const steps = new Map<string, { name: string; analysisId: string }>();
  for (const row of stepResult.data ?? []) {
    steps.set(row.analysis_step_id, {
      name:
        row.analysisstep_setting?.display_name?.trim() ||
        row.analysis_step_name ||
        row.analysis_step_id,
      analysisId: row.analysis_id,
    });
  }
  return { analyses, steps };
}

export function toInputResultLabels(
  variables: InputVariableRow[],
  values: InputVariableValueRow[],
  referenceLookups?: InputResultReferenceLookups,
): InputResultLabel[] {
  const valuesByVariableId = new Map(
    values.map((row) => [row.variable_id, row]),
  );
  const childrenByParentId = new Map<string, InputVariableRow[]>();
  for (const variable of variables) {
    if (!variable.parent_variable_id) continue;
    const siblings = childrenByParentId.get(variable.parent_variable_id) ?? [];
    siblings.push(variable);
    childrenByParentId.set(variable.parent_variable_id, siblings);
  }

  const toLabel = (
    variable: InputVariableRow,
    row: InputVariableValueRow | undefined,
  ): SortableInputResultLabel | null => {
    if (!row || variable.variable_is_visible === false) return null;
    if (
      referenceLookups &&
      (variable.variable_linked_table === "analysis" ||
        variable.variable_linked_table === "analysisstep")
    ) {
      const ids = Array.isArray(row.variable_value)
        ? [
            ...new Set(
              row.variable_value.filter(
                (item): item is string => typeof item === "string",
              ),
            ),
          ]
        : [];
      const references = ids.map((id): InputResultReference => {
        if (variable.variable_linked_table === "analysis") {
          const match = referenceLookups.analyses.get(id);
          return {
            id,
            name: match?.name ?? id,
            ...(match ? { href: `/reports/${id}` } : {}),
          };
        }
        const match = referenceLookups.steps.get(id);
        return {
          id,
          name: match?.name ?? id,
          ...(match
            ? {
                href: `/reports/${match.analysisId}#report-step-${id}`,
              }
            : {}),
        };
      });
      return {
        key: variable.variable_name_frontend?.trim() || variable.variable_name,
        value: `${references.length} selected`,
        references,
        group: variable.variable_group?.trim() ?? "",
        order: variable.variable_order ?? Number.MAX_SAFE_INTEGER,
      };
    }
    const value = serializeInputResultValue(row.variable_value);
    if (value === null) return null;
    return {
      key: variable.variable_name_frontend?.trim() || variable.variable_name,
      value,
      group: variable.variable_group?.trim() ?? "",
      order: variable.variable_order ?? Number.MAX_SAFE_INTEGER,
    };
  };

  const sortLabels = (
    a: SortableInputResultLabel,
    b: SortableInputResultLabel,
  ) =>
    a.group.localeCompare(b.group) ||
    a.order - b.order ||
    a.key.localeCompare(b.key);
  const stripSortFields = ({
    key,
    value,
    references,
    children,
  }: SortableInputResultLabel): InputResultLabel => ({
    key,
    value,
    ...(references ? { references } : {}),
    ...(children ? { children } : {}),
  });

  return variables
    .filter((variable) => !variable.parent_variable_id)
    .flatMap<SortableInputResultLabel>((variable) => {
      const row = valuesByVariableId.get(variable.variable_id);
      const childVariables = childrenByParentId.get(variable.variable_id);
      if (!childVariables) {
        const label = toLabel(variable, row);
        return label ? [label] : [];
      }
      if (row?.variable_value !== true) return [];
      const parentLabel = toLabel(variable, row);
      if (!parentLabel) return [];
      const children = childVariables
        .flatMap<SortableInputResultLabel>((childVariable) => {
          const childLabel = toLabel(
            childVariable,
            valuesByVariableId.get(childVariable.variable_id),
          );
          return childLabel ? [childLabel] : [];
        })
        .sort(sortLabels)
        .map(stripSortFields);
      return [{ ...parentLabel, children }];
    })
    .sort(sortLabels)
    .map(stripSortFields);
}

export type ReportDetailPresentation = {
  hasSubanalysisInvocation?: boolean;
  analysisSetting: {
    display_name: string;
    display_description: string | null;
    user_context?: string | null;
  };
  variables: InputVariableRow[];
  variableValues: InputVariableValueRow[];
  referenceLookups: InputResultReferenceLookups;
  templateOrigin?: TemplateOrigin;
};

export type UiReportDetail = AnalysisRow & {
  hasSubanalysisInvocation?: boolean;
  displayName: string;
  context?: string;
  displayDescription?: string;
  userContext?: string;
  displayInputResults: InputResultLabel[];
  templateOrigin?: TemplateOrigin;
};

async function fetchReportRuntime(id: string): Promise<AnalysisRow> {
  const { data, error } = await analysisClient
    .from("analysis")
    .select("*")
    .eq("analysis_id", id)
    .single();
  if (error) throw error;
  return data;
}

async function fetchReportPresentation(
  id: string,
  templateId?: string | null,
): Promise<ReportDetailPresentation> {
  const [settingResult, variablesResult, valuesResult, rolesResult] =
    await Promise.all([
      analysisClient
        .from("analysis_setting")
        .select("display_name, display_description, user_context")
        .eq("analysis_id", id)
        .single(),
      analysisClient
        .from("variable")
        .select("*")
        .eq("analysis_id", id)
        .is("analysis_step_id", null),
      analysisClient
        .from("variable_value")
        .select("*")
        .eq("analysis_id", id)
        .is("analysis_step_id", null),
      analysisClient
        .from("analysisstep")
        .select(
          "variables:variable!variable_analysis_step_id_fkey(variable_role)",
        )
        .eq("analysis_id", id),
    ]);
  if (rolesResult.error) throw rolesResult.error;
  if (settingResult.error) throw settingResult.error;
  if (variablesResult.error) throw variablesResult.error;
  if (valuesResult.error) throw valuesResult.error;
  const variables = (variablesResult.data ?? []) as InputVariableRow[];
  const variableValues = (valuesResult.data ?? []) as InputVariableValueRow[];
  let templateOrigin: TemplateOrigin | undefined;
  if (templateId) {
    const sourceResult = await analysisClient
      .from("analysis")
      .select("analysis_id, analysis_name, analysis_setting(display_name)")
      .eq("analysis_id", templateId)
      .maybeSingle();
    if (sourceResult.error) throw sourceResult.error;
    const source = sourceResult.data as {
      analysis_id: string;
      analysis_name: string;
      analysis_setting: { display_name: string | null } | null;
    } | null;
    if (source) {
      templateOrigin = toTemplateOrigin({
        id: source.analysis_id,
        displayName: source.analysis_setting?.display_name,
        technicalName: source.analysis_name,
      });
    }
  }
  return {
    hasSubanalysisInvocation: hasSubanalysisInvocation([
      ...(variablesResult.data ?? []),
      ...(rolesResult.data ?? []).flatMap((step) => step.variables),
    ]),
    analysisSetting: settingResult.data,
    variables,
    variableValues,
    templateOrigin,
    referenceLookups: await fetchInputResultReferenceLookups(
      variables,
      variableValues,
    ),
  };
}

export function toUiDetail(
  runtime: AnalysisRow,
  presentation: ReportDetailPresentation,
): UiReportDetail {
  const displayName = presentation.analysisSetting.display_name.trim();
  if (!displayName) {
    throw new Error(
      `Report ${runtime.analysis_id} is missing analysis_setting.display_name`,
    );
  }
  const displayDescription = extractContext(
    presentation.analysisSetting.display_description,
  );
  const userContext = extractContext(presentation.analysisSetting.user_context);
  return {
    ...runtime,
    hasSubanalysisInvocation: presentation.hasSubanalysisInvocation ?? false,
    displayName,
    context: displayDescription,
    displayDescription,
    userContext,
    displayInputResults: toInputResultLabels(
      presentation.variables,
      presentation.variableValues,
      presentation.referenceLookups,
    ),
    templateOrigin: presentation.templateOrigin,
  };
}

export type ReportStepIdentity = Pick<
  AnalysisStepRow,
  "analysis_step_id" | "template_step_id"
>;

export type ReportStepPresentation = {
  displayNames: Map<string, string | null>;
  variables: InputVariableRow[];
  variableValues: InputVariableValueRow[];
  referenceLookups: InputResultReferenceLookups;
  templateOrigins: Map<string, TemplateOrigin>;
};

async function fetchReportStepRuntime(
  analysisId: string,
): Promise<AnalysisStepRow[]> {
  const { data, error } = await analysisClient
    .from("analysisstep")
    .select("*")
    .eq("analysis_id", analysisId)
    .order("analysis_order", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

async function fetchReportStepPresentation(
  steps: ReportStepIdentity[],
): Promise<ReportStepPresentation> {
  const stepIds = steps.map((step) => step.analysis_step_id);
  if (stepIds.length === 0) {
    return {
      displayNames: new Map(),
      variables: [],
      variableValues: [],
      referenceLookups: { analyses: new Map(), steps: new Map() },
      templateOrigins: new Map(),
    };
  }
  const [settingsResult, variablesResult, valuesResult] = await Promise.all([
    analysisClient
      .from("analysisstep_setting")
      .select("analysis_step_id, display_name")
      .in("analysis_step_id", stepIds),
    analysisClient.from("variable").select("*").in("analysis_step_id", stepIds),
    analysisClient
      .from("variable_value")
      .select("*")
      .in("analysis_step_id", stepIds),
  ]);
  if (settingsResult.error) throw settingsResult.error;
  if (variablesResult.error) throw variablesResult.error;
  if (valuesResult.error) throw valuesResult.error;
  const displayNames = new Map(
    (settingsResult.data ?? []).map((setting) => [
      setting.analysis_step_id,
      setting.display_name,
    ]),
  );
  const variables = (variablesResult.data ?? []) as InputVariableRow[];
  const values = (valuesResult.data ?? []) as InputVariableValueRow[];
  const sourceStepIds = [
    ...new Set(
      steps.flatMap((step) =>
        step.template_step_id ? [step.template_step_id] : [],
      ),
    ),
  ];
  const templateOrigins = new Map<string, TemplateOrigin>();
  if (sourceStepIds.length > 0) {
    const sourceStepsResult = await analysisClient
      .from("analysisstep")
      .select(
        "analysis_step_id, analysis_id, analysis_step_name, analysisstep_setting(display_name)",
      )
      .in("analysis_step_id", sourceStepIds);
    if (sourceStepsResult.error) throw sourceStepsResult.error;
    const sourceSteps = (sourceStepsResult.data ?? []) as unknown as Array<{
      analysis_step_id: string;
      analysis_id: string;
      analysis_step_name: string;
      analysisstep_setting: { display_name: string | null } | null;
    }>;
    sourceSteps.forEach((sourceStep) => {
      templateOrigins.set(
        sourceStep.analysis_step_id,
        toTemplateOrigin({
          id: sourceStep.analysis_step_id,
          parentId: sourceStep.analysis_id,
          displayName: sourceStep.analysisstep_setting?.display_name,
          technicalName: sourceStep.analysis_step_name,
        }),
      );
    });
  }
  const referenceLookups = await fetchInputResultReferenceLookups(
    variables,
    values,
  );
  return {
    displayNames,
    variables,
    variableValues: values,
    referenceLookups,
    templateOrigins,
  };
}

export function toUiSteps(
  steps: AnalysisStepRow[],
  presentation: ReportStepPresentation,
): UiReportStepRow[] {
  return steps.map((step) => ({
    ...step,
    display_name: presentation.displayNames.get(step.analysis_step_id) ?? null,
    templateOrigin: step.template_step_id
      ? presentation.templateOrigins.get(step.template_step_id)
      : undefined,
    displayInputResults: toInputResultLabels(
      presentation.variables.filter(
        (row) => row.analysis_step_id === step.analysis_step_id,
      ),
      presentation.variableValues.filter(
        (row) => row.analysis_step_id === step.analysis_step_id,
      ),
      presentation.referenceLookups,
    ),
  }));
}

async function fetchTemplateReportTypes(
  currentTenantId: string,
): Promise<string[]> {
  const templateItems = await fetchTemplates(
    { search: "", showGlobal: true },
    currentTenantId,
  );
  return [
    ...new Set(
      templateItems
        .map(toTemplateUiItem)
        .map((template) => template.type)
        .filter((type): type is string => typeof type === "string"),
    ),
  ];
}

export const reportQueries = {
  all: getKeyOnlyQueryConfig(["reports"]),

  lists: () => getKeyOnlyQueryConfig(["reports", "list"]),

  list: (options: FilterOptions, currentTenantId?: string) =>
    queryOptions({
      queryKey: [...reportQueries.lists().queryKey, options, currentTenantId],
      queryFn: () => fetchReports(options, currentTenantId as string),
      select: (data) => data.map(toUiItem),
      enabled: Boolean(currentTenantId),
    }),

  details: () => getKeyOnlyQueryConfig(["reports", "detail"]),

  detail: (id: string, isPollingEnabled: boolean = true) =>
    queryOptions({
      queryKey: [...reportQueries.details().queryKey, id],
      queryFn: () => fetchReportRuntime(id),
      enabled: Boolean(id),
      refetchInterval: isPollingEnabled ? 5000 : false,
      refetchIntervalInBackground: isPollingEnabled,
    }),

  detailPresentations: () =>
    getKeyOnlyQueryConfig(["reports", "detail-presentation"]),

  detailPresentation: (id: string, templateId?: string | null) =>
    queryOptions({
      queryKey: [
        ...reportQueries.detailPresentations().queryKey,
        id,
        templateId ?? null,
      ],
      queryFn: () => fetchReportPresentation(id, templateId),
      enabled: Boolean(id),
      staleTime: Infinity,
    }),

  stepsKey: () => getKeyOnlyQueryConfig(["reports", "steps"]),

  steps: (analysisId: string, isPollingEnabled: boolean = true) =>
    queryOptions({
      queryKey: [...reportQueries.stepsKey().queryKey, analysisId],
      queryFn: () => fetchReportStepRuntime(analysisId),
      enabled: Boolean(analysisId),
      refetchInterval: isPollingEnabled ? 5000 : false,
      refetchIntervalInBackground: isPollingEnabled,
    }),

  stepPresentations: () =>
    getKeyOnlyQueryConfig(["reports", "step-presentation"]),

  stepPresentation: (analysisId: string, steps: ReportStepIdentity[]) =>
    queryOptions({
      queryKey: [
        ...reportQueries.stepPresentations().queryKey,
        analysisId,
        steps
          .map((step) => [step.analysis_step_id, step.template_step_id ?? null])
          .sort(([left], [right]) => String(left).localeCompare(String(right))),
      ],
      queryFn: () => fetchReportStepPresentation(steps),
      enabled: Boolean(analysisId),
      staleTime: Infinity,
    }),

  templateTypesKey: () => getKeyOnlyQueryConfig(["reports", "template-types"]),

  templateTypes: (currentTenantId?: string) =>
    queryOptions({
      queryKey: [...reportQueries.templateTypesKey().queryKey, currentTenantId],
      queryFn: () => fetchTemplateReportTypes(currentTenantId as string),
      enabled: Boolean(currentTenantId),
    }),
};
